/**
 * PlaceInput — typeahead for birth_place / death_place / marriage_place.
 *
 * Drop-in replacement for a plain <input type="text">. Same props
 * shape (value, onChange(newValue), placeholder, className), with an
 * absolutely-positioned dropdown that lists existing places matching
 * what the user has typed so far. Picking a suggestion sets the value
 * to that place's canonical name; typing free-form still works for
 * places the platform hasn't indexed yet.
 *
 * Suggestions come from /api/places/suggest which is backed by the
 * pg_trgm index on kg_places. Debounced 200ms so we don't hammer the
 * endpoint on every keystroke. Keyboard support: ArrowUp / ArrowDown
 * to navigate, Enter to pick, Escape to close.
 */
import { useEffect, useRef, useState } from 'react'
import { api } from '../../services/api.js'

const FLAG = (cc) => {
  if (!cc || cc.length !== 2) return ''
  const A = 0x1F1E6, base = 0x41
  return String.fromCodePoint(A + cc.toUpperCase().charCodeAt(0) - base) +
         String.fromCodePoint(A + cc.toUpperCase().charCodeAt(1) - base)
}

export default function PlaceInput({
  value,
  onChange,
  placeholder,
  className = 'form-input',
  ...rest
}) {
  const [open, setOpen]           = useState(false)
  const [suggestions, setSugg]    = useState([])
  const [active, setActive]       = useState(0)
  const [loading, setLoading]     = useState(false)
  const debounceRef = useRef(null)
  const wrapperRef  = useRef(null)
  const inputRef    = useRef(null)

  // Debounced fetch. Skip when value is short (<2 chars) or matches an
  // exact suggestion the user already picked.
  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current)
    const q = (value || '').trim()
    if (q.length < 2) { setSugg([]); return }
    debounceRef.current = setTimeout(async () => {
      setLoading(true)
      try {
        const res = await api.get(`/places/suggest?q=${encodeURIComponent(q)}`)
        setSugg(Array.isArray(res) ? res : [])
        setActive(0)
      } catch {
        setSugg([])
      } finally {
        setLoading(false)
      }
    }, 200)
    return () => clearTimeout(debounceRef.current)
  }, [value])

  // Close on outside click.
  useEffect(() => {
    function handle(e) {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target)) setOpen(false)
    }
    document.addEventListener('mousedown', handle)
    return () => document.removeEventListener('mousedown', handle)
  }, [])

  function pick(s) {
    onChange(s.label)
    setOpen(false)
    setSugg([])
    inputRef.current?.blur()
  }

  function handleKeyDown(e) {
    if (!open || suggestions.length === 0) return
    if (e.key === 'ArrowDown') {
      e.preventDefault()
      setActive(i => Math.min(i + 1, suggestions.length - 1))
    } else if (e.key === 'ArrowUp') {
      e.preventDefault()
      setActive(i => Math.max(i - 1, 0))
    } else if (e.key === 'Enter') {
      if (suggestions[active]) {
        e.preventDefault()
        pick(suggestions[active])
      }
    } else if (e.key === 'Escape') {
      setOpen(false)
    }
  }

  const showDropdown = open && suggestions.length > 0

  return (
    <div className="place-input" ref={wrapperRef}>
      <input
        ref={inputRef}
        type="text"
        className={className}
        value={value || ''}
        placeholder={placeholder}
        onChange={e => { onChange(e.target.value); setOpen(true) }}
        onFocus={() => setOpen(true)}
        onKeyDown={handleKeyDown}
        autoComplete="off"
        {...rest}
      />
      {showDropdown && (
        <ul className="place-input__menu" role="listbox">
          {suggestions.map((s, i) => (
            <li
              key={`${s.label}|${s.latitude}|${s.longitude}`}
              role="option"
              aria-selected={i === active}
              className={`place-input__option${i === active ? ' is-active' : ''}`}
              onMouseDown={(e) => { e.preventDefault(); pick(s) }}
              onMouseEnter={() => setActive(i)}
            >
              <span className="place-input__flag">{FLAG(s.country_code)}</span>
              <span className="place-input__label">{s.label}</span>
            </li>
          ))}
        </ul>
      )}
      {/* The "no matches" hint was more annoying than useful — when a
          user edits an existing person whose place is already saved,
          they're not currently typing, but the hint still showed up
          and made the value look wrong. Just hide when there's
          nothing to suggest; the input itself is a free-text field
          and that's the contract regardless. */}
    </div>
  )
}
